import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Table, Row, Col, Statistic, Tabs, Alert, DatePicker, message, Modal, Spin, Input } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarOutlined, TruckOutlined, FundOutlined, CarOutlined, BarChartOutlined, AuditOutlined, FileTextOutlined, FilePdfOutlined, RobotOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import ReactMarkdown from 'react-markdown';
import ApiService from '../services/api';
import ProfitLoss from './ProfitLoss';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const Reports = () => {
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [expensesData, setExpensesData] = useState(null);
  const [fuelData, setFuelData] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditView, setAuditView] = useState('month');
  const [activeTab, setActiveTab] = useState('revenue');
  const [userType, setUserType] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [analysisModal, setAnalysisModal] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');

  useEffect(() => {
    getCurrentUser();
  }, []);

  const getCurrentUser = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const type = user.attributes['custom:user_type'];
      setUserType(type);
    } catch (error) {
      console.error('Error getting user:', error);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const data = await ApiService.getDashboardData();
      setDrivers(data.drivers || []);
      setTrucks(data.trucks || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  };

  const generateReports = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range');
      return;
    }
    
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      
      const isAdmin = userType === 'admin';
      const [revenue, expenses, fuel] = await Promise.all([
        ApiService.getGrossIncomeReport(null, startDate, endDate, selectedTruck, isAdmin),
        ApiService.getExpensesReport(null, startDate, endDate, selectedTruck, isAdmin),
        ApiService.getFuelConsumptionReport(null, startDate, endDate, selectedTruck, isAdmin)
      ]);
      
      console.log('Expenses API Response:', expenses);
      console.log('Fuel Records from API:', expenses?.fuel_records);
      console.log('Expense Records from API:', expenses?.expense_records);
      
      setRevenueData(revenue);
      setExpensesData(expenses);
      setFuelData(fuel);
      generateAuditData(revenue, expenses, fuel, startDate, endDate);
    } catch (error) {
      console.error('Error generating reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAIAnalysis = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range first');
      return;
    }
    
    setAnalysisLoading(true);
    setAnalysisModal(true);
    setAnalysisData(null);
    
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const response = await ApiService.getAIAnalysis(startDate, endDate, selectedTruck, customPrompt || null);
      setAnalysisData(response);
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      message.error('Failed to generate AI analysis');
      setAnalysisModal(false);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const downloadAIAnalysisPDF = async () => {
    if (!analysisData) return;
    
    setPdfLoading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      let yPosition = 20;
      
      const startDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : 'N/A';
      const endDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : 'N/A';
      const selectedTruckName = selectedTruck ? 
        trucks.find(t => t.truck_id === selectedTruck)?.license_plate || 'Unknown Truck' : 'All Trucks';
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DAN-CHI LOGISTICS', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(16);
      pdf.text('AI Operations Analysis Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Report details
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${startDate} to ${endDate}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Scope: ${selectedTruckName}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 10;
      
      // Divider
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      
      // Summary section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FINANCIAL SUMMARY', margin, yPosition);
      yPosition += 10;
      
      const summary = analysisData.summary || {};
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const totalRev = Number(summary.total_revenue) || 0;
      const totalFuel = Number(summary.total_fuel) || 0;
      const totalExp = Number(summary.total_expenses) || 0;
      const netProf = Number(summary.net_profit) || 0;
      const profMarg = Number(summary.profit_margin) || 0;
      
      const summaryItems = [
        ['Total Revenue:', `$${totalRev.toFixed(2)}`],
        ['Total Fuel:', `$${totalFuel.toFixed(2)}`],
        ['Total Expenses:', `$${totalExp.toFixed(2)}`],
        ['Net Profit:', `$${netProf.toFixed(2)}`],
        ['Profit Margin:', `${profMarg.toFixed(1)}%`]
      ];
      
      summaryItems.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'normal');
        pdf.text(label, margin, yPosition);
        pdf.setFont('helvetica', 'bold');
        pdf.text(value, margin + 50, yPosition);
        yPosition += 7;
      });
      
      yPosition += 8;
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      
      // AI Analysis section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI INSIGHTS', margin, yPosition);
      yPosition += 10;
      
      // Process markdown text to plain text for PDF
      const analysisText = analysisData.analysis || '';
      const lines = analysisText.split('\n');
      
      pdf.setFontSize(10);
      
      for (const line of lines) {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          yPosition = 20;
        }
        
        let processedLine = line.trim();
        if (!processedLine) {
          yPosition += 4;
          continue;
        }
        
        // Handle headers
        if (processedLine.startsWith('## ')) {
          yPosition += 4;
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          processedLine = processedLine.replace('## ', '');
          pdf.text(processedLine, margin, yPosition);
          yPosition += 8;
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          continue;
        }
        
        if (processedLine.startsWith('### ')) {
          yPosition += 2;
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          processedLine = processedLine.replace('### ', '');
          pdf.text(processedLine, margin, yPosition);
          yPosition += 7;
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          continue;
        }
        
        // Remove markdown formatting
        processedLine = processedLine
          .replace(/\*\*/g, '')
          .replace(/\*/g, '')
          .replace(/`/g, '');
        
        // Handle bullet points
        if (processedLine.startsWith('- ') || processedLine.startsWith('• ')) {
          processedLine = '  • ' + processedLine.substring(2);
        }
        
        // Word wrap long lines
        const maxWidth = pageWidth - (margin * 2);
        const splitLines = pdf.splitTextToSize(processedLine, maxWidth);
        
        for (const splitLine of splitLines) {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(splitLine, margin, yPosition);
          yPosition += 5;
        }
      }
      
      // Footer on last page
      yPosition = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.text('AI-Generated Analysis - DAN-CHI Logistics', pageWidth / 2, yPosition, { align: 'center' });
      
      const fileName = `AI_Analysis_${startDate}_to_${endDate}.pdf`;
      pdf.save(fileName);
      message.success('AI Analysis report downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const formatAnalysisText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, idx) => {
      if (line.match(/^\*\*.*\*\*:?$/)) {
        return <h4 key={idx} style={{ color: '#1890ff', marginTop: 16, marginBottom: 8 }}>{line.replace(/\*\*/g, '')}</h4>;
      }
      if (line.startsWith('- ') || line.startsWith('• ') || line.match(/^\d+\./)) {
        return <p key={idx} style={{ marginLeft: 16, marginBottom: 4 }}>{line}</p>;
      }
      if (line.trim()) {
        return <p key={idx} style={{ marginBottom: 8 }}>{line}</p>;
      }
      return <br key={idx} />;
    });
  };

  const generateAuditData = (revenue, expenses, fuel, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const monthlyData = generateMonthlyAudit(revenue, expenses, fuel, start, end);
    const weeklyData = generateWeeklyAudit(revenue, expenses, fuel, start, end);
    const dailyData = generateDailyAudit(revenue, expenses, fuel, start, end);
    
    setAuditData({
      month: monthlyData,
      week: weeklyData,
      day: dailyData
    });
  };

  const downloadPeriodPDF = async () => {
    if (!auditData || !auditData[auditView] || auditData[auditView].length === 0) {
      message.error('No data available for the selected period');
      return;
    }

    setPdfLoading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      let yPosition = 20;
      const data = auditData[auditView];
      
      // Calculate totals
      const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
      const totalFuel = data.reduce((sum, item) => sum + item.fuel, 0);
      const totalExpenses = data.reduce((sum, item) => sum + item.expenses, 0);
      const netProfit = totalRevenue - totalFuel - totalExpenses;

      // Header with company branding (same as P&L)
      const startDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : 'N/A';
      const endDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : 'N/A';
      const selectedTruckName = selectedTruck ? 
        trucks.find(t => t.truck_id === selectedTruck)?.license_plate || 'Unknown Truck' : 'All Trucks';
      const driverName = selectedTruck ? 
        drivers.find(d => d.truck_id === selectedTruck)?.name || '' : 'All Drivers';
      
      // Company header section (matching P&L format)
      const headerSection = {
        textAlign: 'center',
        marginBottom: '40px',
        borderBottom: '2px solid #333',
        paddingBottom: '30px'
      };
      
      // Logo placeholder and company name
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DAN-CHI LOGISTICS', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      pdf.setFontSize(16);
      pdf.text(`${auditView === 'day' ? 'Daily' : auditView.charAt(0).toUpperCase() + auditView.slice(1)}ly Activity Report`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;
      
      // Report details section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} - ${new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Scope: ${selectedTruckName}${driverName !== 'All Drivers' ? ` (${driverName})` : ''}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 20, yPosition);
      yPosition += 20;
      
      // Draw header border
      pdf.setLineWidth(0.5);
      pdf.line(20, yPosition - 5, pageWidth - 20, yPosition - 5);
      yPosition += 10;

      // Summary cards section (matching P&L format)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PERIOD SUMMARY', 20, yPosition);
      yPosition += 15;
      
      // Summary boxes with borders
      const boxWidth = 40;
      const boxHeight = 20;
      const boxSpacing = 45;
      const startX = 20;
      
      // Revenue box
      pdf.rect(startX, yPosition, boxWidth, boxHeight);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Revenue', startX + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${totalRevenue.toFixed(2)}`, startX + 2, yPosition + 15);
      
      // Expenses box
      pdf.rect(startX + boxSpacing, yPosition, boxWidth, boxHeight);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Expenses', startX + boxSpacing + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${(totalFuel + totalExpenses).toFixed(2)}`, startX + boxSpacing + 2, yPosition + 15);
      
      // Net Profit box (highlighted)
      pdf.setLineWidth(1);
      pdf.rect(startX + (boxSpacing * 2), yPosition, boxWidth, boxHeight);
      pdf.setLineWidth(0.5);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Net Profit', startX + (boxSpacing * 2) + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${netProfit.toFixed(2)}`, startX + (boxSpacing * 2) + 2, yPosition + 15);
      
      // Profit Margin box
      const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(2) : 0;
      pdf.rect(startX + (boxSpacing * 3), yPosition, boxWidth, boxHeight);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Profit Margin', startX + (boxSpacing * 3) + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${profitMargin}%`, startX + (boxSpacing * 3) + 2, yPosition + 15);
      
      yPosition += boxHeight + 20;

      // Financial Statement Table (matching P&L format)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FINANCIAL BREAKDOWN', 20, yPosition);
      yPosition += 15;
      
      // Table with borders
      const tableStartY = yPosition;
      const colWidths = [60, 30, 30, 30, 40];
      const colX = [20, 80, 110, 140, 170];
      const rowHeight = 8;
      
      // Table header
      pdf.setFillColor(245, 245, 245);
      pdf.rect(20, yPosition, 170, rowHeight, 'F');
      pdf.rect(20, yPosition, 170, rowHeight);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Period', colX[0] + 2, yPosition + 6);
      pdf.text('Revenue', colX[1] + 2, yPosition + 6);
      pdf.text('Fuel', colX[2] + 2, yPosition + 6);
      pdf.text('Expenses', colX[3] + 2, yPosition + 6);
      pdf.text('Net Profit', colX[4] + 2, yPosition + 6);
      
      // Vertical lines for header
      for (let i = 1; i < colX.length; i++) {
        pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
      }
      yPosition += rowHeight;
      
      // Table rows
      pdf.setFont('helvetica', 'normal');
      data.forEach((item, index) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        const itemProfit = item.revenue - item.fuel - item.expenses;
        
        // Alternating row colors
        if (index % 2 === 1) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(20, yPosition, 170, rowHeight, 'F');
        }
        
        // Row borders
        pdf.rect(20, yPosition, 170, rowHeight);
        for (let i = 1; i < colX.length; i++) {
          pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
        }
        
        // Row data
        pdf.text(item.name.length > 18 ? item.name.substring(0, 18) + '...' : item.name, colX[0] + 2, yPosition + 6);
        pdf.text(`$${item.revenue.toFixed(2)}`, colX[1] + 2, yPosition + 6);
        pdf.text(`$${item.fuel.toFixed(2)}`, colX[2] + 2, yPosition + 6);
        pdf.text(`$${item.expenses.toFixed(2)}`, colX[3] + 2, yPosition + 6);
        pdf.text(`$${itemProfit.toFixed(2)}`, colX[4] + 2, yPosition + 6);
        yPosition += rowHeight;
      });
      
      // Totals row (highlighted)
      pdf.setFillColor(240, 240, 240);
      pdf.rect(20, yPosition, 170, rowHeight, 'F');
      pdf.setLineWidth(1);
      pdf.rect(20, yPosition, 170, rowHeight);
      pdf.setLineWidth(0.5);
      
      for (let i = 1; i < colX.length; i++) {
        pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('TOTALS', colX[0] + 2, yPosition + 6);
      pdf.text(`$${totalRevenue.toFixed(2)}`, colX[1] + 2, yPosition + 6);
      pdf.text(`$${totalFuel.toFixed(2)}`, colX[2] + 2, yPosition + 6);
      pdf.text(`$${totalExpenses.toFixed(2)}`, colX[3] + 2, yPosition + 6);
      pdf.text(`$${netProfit.toFixed(2)}`, colX[4] + 2, yPosition + 6);
      yPosition += rowHeight + 30;

      // Footer section (matching P&L format)
      if (yPosition > 220) {
        pdf.addPage();
        yPosition = 20;
      }
      
      // Report Information section
      pdf.setLineWidth(0.5);
      pdf.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 15;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('ACKNOWLEDGMENT', 20, yPosition);
      yPosition += 15;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('I acknowledge that the above information is accurate and complete.', 20, yPosition);
      yPosition += 20;
      
      pdf.text('Signature: ________________________', 20, yPosition);
      pdf.text('Date: ________________________', 120, yPosition);
      yPosition += 30;
      
      // Company footer (matching P&L format)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Report Information', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
      pdf.text(`Report Type: ${auditView === 'day' ? 'Daily' : auditView.charAt(0).toUpperCase() + auditView.slice(1)}ly Activity Report`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
      pdf.setFont('helvetica', 'italic');
      pdf.text('Confidential Financial Report', pageWidth / 2, yPosition, { align: 'center' });

      const fileName = `${auditView === 'day' ? 'Daily' : auditView.charAt(0).toUpperCase() + auditView.slice(1)}ly_Report_${startDate}_to_${endDate}.pdf`;
      pdf.save(fileName);
      message.success('Period report downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const generateMonthlyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    const monthlyData = {};
    
    // Group trips by month
    trips.forEach(trip => {
      const month = trip.trip_date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].revenue += trip.total_revenue || 0;
    });
    
    // Group fuel by month
    fuelRecords.forEach(record => {
      const month = record.date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].fuel += record.amount_spent || 0;
    });
    
    // Group expenses by month
    expenseRecords.forEach(record => {
      const month = record.date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].expenses += record.amount || 0;
    });
    
    return Object.entries(monthlyData).map(([month, data]) => ({
      period: month,
      name: new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel
    }));
  };

  const generateWeeklyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    const weeklyData = {};
    
    const getWeekKey = (date) => {
      const d = new Date(date);
      const week = Math.ceil(d.getDate() / 7);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-W${week}`;
    };
    
    // Group trips by week
    trips.forEach(trip => {
      const week = getWeekKey(trip.trip_date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].revenue += trip.total_revenue || 0;
    });
    
    // Group fuel by week
    fuelRecords.forEach(record => {
      const week = getWeekKey(record.date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].fuel += record.amount_spent || 0;
    });
    
    // Group expenses by week
    expenseRecords.forEach(record => {
      const week = getWeekKey(record.date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].expenses += record.amount || 0;
    });
    
    return Object.entries(weeklyData).map(([week, data]) => ({
      period: week,
      name: `Week ${week.split('-W')[1]} of ${week.split('-')[1]}/${week.split('-')[0]}`,
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel
    }));
  };

  const generateDailyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    console.log('=== DAILY AUDIT DEBUG ===');
    console.log('Revenue object:', revenue);
    console.log('Expenses object:', expenses);
    console.log('Fuel object:', fuel);
    console.log('Extracted fuelRecords:', fuelRecords);
    console.log('Extracted expenseRecords:', expenseRecords);
    console.log('Extracted trips:', trips.length);
    
    const dailyData = {};
    
    // Group trips by day
    trips.forEach(trip => {
      const day = trip.trip_date;
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [] };
      }
      dailyData[day].revenue += trip.total_revenue || 0;
      dailyData[day].trips.push(trip);
    });
    
    // Group fuel by day
    fuelRecords.forEach(record => {
      const day = record.date;
      console.log('Processing fuel record for day:', day, record);
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [] };
      }
      dailyData[day].fuel += record.amount_spent || 0;
      dailyData[day].fuelRecords.push(record);
    });
    
    // Group expenses by day
    expenseRecords.forEach(record => {
      const day = record.date;
      console.log('Processing expense record for day:', day, record);
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [] };
      }
      dailyData[day].expenses += record.amount || 0;
      dailyData[day].expenseRecords.push(record);
    });
    
    const result = Object.entries(dailyData).map(([day, data]) => ({
      period: day,
      name: new Date(day).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel,
      trips: data.trips,
      fuelRecords: data.fuelRecords,
      expenseRecords: data.expenseRecords
    }));
    
    console.log('Daily Audit Result:', result);
    return result;
  };

  const renderRevenueTab = () => {
    if (!revenueData) return <div>Click "Generate Reports" to view revenue data</div>;

    // Single driver view
    if (revenueData.driver_id) {
      return (
        <div>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Total Revenue" 
                  value={revenueData.total_revenue || 0} 
                  prefix="$" 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Total Trips" 
                  value={revenueData.trips?.length || 0} 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Avg Revenue/Trip" 
                  value={revenueData.trips?.length > 0 ? (revenueData.total_revenue / revenueData.trips.length).toFixed(2) : 0} 
                  prefix="$" 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Driver" 
                  value={revenueData.driver_name} 
                  valueStyle={{ fontSize: '16px' }}
                />
              </Card>
            </Col>
          </Row>


        </div>
      );
    }

    // All drivers view
    if (revenueData.revenue_by_driver) {
      const chartData = Object.entries(revenueData.revenue_by_driver).map(([driver, revenue]) => ({
        driver,
        revenue
      }));

      const totalRevenue = Object.values(revenueData.revenue_by_driver).reduce((sum, rev) => sum + rev, 0);

      return (
        <div>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic title="Total Fleet Revenue" value={totalRevenue} prefix="$" />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title="Active Drivers" value={chartData.length} />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title="Total Trips" value={revenueData.trips?.length || 0} />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Card title="Revenue by Driver">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="driver" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="revenue" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Revenue Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revenue"
                      label={({ driver, percent }) => `${driver} ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title="Driver Performance Summary">
            <Table 
              dataSource={chartData} 
              rowKey="driver"
              columns={[
                { title: 'Driver', dataIndex: 'driver', key: 'driver' },
                { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (val) => `$${val}` },
                { title: '% of Total', key: 'percentage', render: (_, record) => `${((record.revenue / totalRevenue) * 100).toFixed(1)}%` }
              ]}
            />
          </Card>
        </div>
      );
    }

    return (
      <Alert message="No revenue data available" type="info" />
    );
  };

  const renderExpensesTab = () => {
    if (!expensesData) return <div>Click "Generate Reports" to view expenses data</div>;

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Fuel Expenses" value={expensesData.fuel_expenses || 0} prefix="$" />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Other Expenses" value={expensesData.other_expenses || 0} prefix="$" />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Total Expenses" value={expensesData.total_expenses || 0} prefix="$" />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Expense Records" 
                value={(expensesData.fuel_records?.length || 0) + (expensesData.expense_records?.length || 0)} 
              />
            </Card>
          </Col>
        </Row>

        {expensesData.fuel_records && expensesData.fuel_records.length > 0 && (
          <Card title="Fuel Expenses" style={{ marginBottom: 24 }}>
            <Table 
              dataSource={expensesData.fuel_records} 
              rowKey="fuel_id"
              columns={[
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Driver', dataIndex: 'driver_name', key: 'driver' },
                { title: 'Liters', dataIndex: 'liters', key: 'liters' },
                { title: 'Amount', dataIndex: 'amount_spent', key: 'amount', render: (val) => `$${val || 0}` },
                { title: 'Status', dataIndex: 'status', key: 'status' }
              ]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        )}

        {expensesData.expense_records && expensesData.expense_records.length > 0 && (
          <Card title="Other Expenses">
            <Table 
              dataSource={expensesData.expense_records} 
              rowKey="expense_id"
              columns={[
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Driver', dataIndex: 'driver_name', key: 'driver' },
                { title: 'Type', dataIndex: 'expense_type', key: 'type' },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `$${val || 0}` },
                { title: 'Description', dataIndex: 'description', key: 'description' }
              ]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        )}

        {(!expensesData.fuel_records || expensesData.fuel_records.length === 0) && 
         (!expensesData.expense_records || expensesData.expense_records.length === 0) && (
          <Alert message="No expense data available" type="info" />
        )}
      </div>
    );
  };

  const renderFuelTab = () => {
    if (!fuelData || !fuelData.fuel_consumption) {
      return <Alert message="No fuel consumption data available" type="info" />;
    }

    const tableData = Object.entries(fuelData.fuel_consumption).map(([truckKey, data]) => ({
      truck: truckKey,
      truck_plate: data.truck_plate,
      drivers: data.drivers,
      total_liters: data.total_liters,
      total_distance: data.total_distance,
      total_cost: data.total_cost,
      fuel_efficiency: data.fuel_efficiency || 'N/A',
      cost_per_km: data.cost_per_km || 'N/A',
      records: data.records || [],
      records_count: data.records?.length || 0
    }));

    const chartData = tableData.map(item => ({
      truck: item.truck_plate || item.truck,
      efficiency: parseFloat(item.fuel_efficiency) || 0,
      cost: item.total_cost
    }));

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Fuel Cost" 
                value={tableData.reduce((sum, item) => sum + (item.total_cost || 0), 0).toFixed(2)} 
                prefix="$" 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Liters" 
                value={tableData.reduce((sum, item) => sum + (item.total_liters || 0), 0).toFixed(3)} 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Distance" 
                value={tableData.reduce((sum, item) => sum + (item.total_distance || 0), 0).toFixed(3)} 
                suffix="km"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Fuel Records" 
                value={tableData.reduce((sum, item) => sum + (item.records_count || 0), 0)} 
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="Fuel Efficiency by Truck">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="truck" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="efficiency" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Fuel Cost by Truck">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="truck" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cost" fill="#ff7300" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Card title="Fuel Consumption by Truck">
          <Table 
            dataSource={tableData} 
            rowKey="truck"
            columns={[
              { title: 'Truck', dataIndex: 'truck_plate', key: 'truck', render: (val, record) => val || record.truck },
              { title: 'Driver(s)', dataIndex: 'drivers', key: 'drivers', render: (drivers) => Array.isArray(drivers) ? drivers.join(', ') : drivers },
              { title: 'Total Liters', dataIndex: 'total_liters', key: 'liters', render: (val) => val?.toFixed(3) },
              { title: 'Distance (km)', dataIndex: 'total_distance', key: 'distance', render: (val) => val?.toFixed(3) },
              { title: 'Total Cost', dataIndex: 'total_cost', key: 'cost', render: (val) => `$${(val || 0).toFixed(2)}` },
              { title: 'Efficiency (km/L)', dataIndex: 'fuel_efficiency', key: 'efficiency' },
              { title: 'Cost per km', dataIndex: 'cost_per_km', key: 'cost_per_km', render: (val) => val !== 'N/A' ? `$${val}` : val },
              { title: 'Records', dataIndex: 'records_count', key: 'records' }
            ]}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '16px' }}>
                  <h4>Fuel Consumption Details</h4>
                  <Table
                    dataSource={record.records}
                    rowKey="fuel_id"
                    columns={[
                      { title: 'Date', dataIndex: 'date', key: 'date', width: 120 },
                      { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name', width: 150 },
                      { title: 'Odometer', dataIndex: 'odometer', key: 'odometer', width: 100, render: (val) => val?.toFixed(3) },
                      { title: 'Distance', dataIndex: 'distance_covered', key: 'distance', width: 100, render: (val) => val ? `${val.toFixed(3)} km` : '-' },
                      { title: 'Liters', dataIndex: 'liters', key: 'liters', width: 80, render: (val) => val?.toFixed(3) },
                      { title: 'Cost', dataIndex: 'amount_spent', key: 'amount', width: 80, render: (val) => `$${(val || 0).toFixed(2)}` },
                      { title: 'Efficiency', dataIndex: 'efficiency', key: 'efficiency', width: 100, render: (val) => val ? `${val} km/L` : '-' },
                      { title: 'Status', dataIndex: 'status', key: 'status', width: 100 }
                    ]}
                    pagination={false}
                    size="small"
                  />
                </div>
              ),
              rowExpandable: (record) => record.records && record.records.length > 0
            }}
          />
        </Card>
      </div>
    );
  };

  const renderProfitabilityTab = () => {
    if (!revenueData || !expensesData) {
      return <Alert message="Generate reports to view profitability analysis" type="info" />;
    }

    const totalRevenue = revenueData.revenue_by_driver ? 
      Object.values(revenueData.revenue_by_driver).reduce((sum, rev) => sum + rev, 0) :
      revenueData.total_revenue || 0;

    const totalExpenses = expensesData.total_expenses || 0;
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(2) : 0;

    const profitData = [
      { name: 'Revenue', value: totalRevenue, color: '#52c41a' },
      { name: 'Expenses', value: totalExpenses, color: '#ff4d4f' }
    ];

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Revenue" value={totalRevenue} prefix="$" />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Total Expenses" value={totalExpenses} prefix="$" />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Net Profit" 
                value={netProfit} 
                prefix="$" 
                valueStyle={{ color: netProfit >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Profit Margin" 
                value={profitMargin} 
                suffix="%" 
                valueStyle={{ color: netProfit >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Card title="Revenue vs Expenses">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={profitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Financial Breakdown">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={profitData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {profitData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderAuditTab = () => {
    if (!auditData) return <div>Click "Generate Reports" to view audit trail</div>;

    const currentData = auditData[auditView] || [];
    
    const columns = [
      { title: 'Period', dataIndex: 'name', key: 'name' },
      { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (val) => `$${val}` },
      { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (val) => `$${val}` },
      { title: 'Fuel Cost', dataIndex: 'fuel', key: 'fuel', render: (val) => `$${val}` },
      { title: 'Net Profit', key: 'profit', render: (_, record) => {
        const profit = record.revenue - record.expenses - record.fuel;
        return (
          <span style={{ color: profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
            ${profit}
          </span>
        );
      }}
    ];

    const chartData = currentData.map(item => ({
      name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
      revenue: item.revenue,
      expenses: item.expenses + item.fuel,
      profit: item.revenue - item.expenses - item.fuel
    }));

    return (
      <div>
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col>
              <Select value={auditView} onChange={setAuditView} style={{ width: 120 }}>
                <Option value="month">Monthly</Option>
                <Option value="week">Weekly</Option>
                <Option value="day">Daily</Option>
              </Select>
            </Col>
            <Col>
              <Button 
                type="primary" 
                icon={<FilePdfOutlined />} 
                onClick={downloadPeriodPDF}
                loading={pdfLoading}
              >
                Download Period Report
              </Button>
            </Col>
          </Row>
        </Card>

        <Row gutter={16} style={{ marginBottom: 24 }}>          
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Revenue" 
                value={currentData.reduce((sum, item) => sum + item.revenue, 0)} 
                prefix="$" 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Expenses" 
                value={currentData.reduce((sum, item) => sum + item.expenses + item.fuel, 0)} 
                prefix="$" 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Net Profit" 
                value={currentData.reduce((sum, item) => sum + item.revenue - item.expenses - item.fuel, 0)} 
                prefix="$" 
                valueStyle={{ color: currentData.reduce((sum, item) => sum + item.revenue - item.expenses - item.fuel, 0) >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Records" 
                value={currentData.length} 
              />
            </Card>
          </Col>
        </Row>

        <Card title={`${auditView.charAt(0).toUpperCase() + auditView.slice(1)}ly Performance`} style={{ marginBottom: 24 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#52c41a" name="Revenue" />
              <Bar dataKey="expenses" fill="#ff4d4f" name="Expenses" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Detailed Breakdown">
          <Table 
            dataSource={currentData} 
            columns={columns} 
            rowKey="period"
            pagination={{ pageSize: 10 }}
            expandable={auditView === 'day' ? {
              expandedRowRender: (record) => (
                <div style={{ padding: '16px' }}>
                  {record.trips && record.trips.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4>Trips ({record.trips.length})</h4>
                      <Table
                        dataSource={record.trips}
                        rowKey={(trip, index) => `${trip.trip_id}-${index}`}
                        columns={[
                          { title: 'Trip ID', dataIndex: 'trip_id', key: 'trip_id', width: 100 },
                          { title: 'Origin', dataIndex: 'origin', key: 'origin' },
                          { title: 'Destination', dataIndex: 'destination', key: 'destination' },
                          { title: 'Price', dataIndex: 'price', key: 'price', render: (val) => `$${val || 0}`, width: 80 },
                          { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' }
                        ]}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  )}
                  {record.fuelRecords && record.fuelRecords.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4>Fuel Records ({record.fuelRecords.length})</h4>
                      <Table
                        dataSource={record.fuelRecords}
                        rowKey="fuel_id"
                        columns={[
                          { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
                          { title: 'Liters', dataIndex: 'liters', key: 'liters', width: 80 },
                          { title: 'Amount', dataIndex: 'amount_spent', key: 'amount_spent', render: (val) => `$${val || 0}`, width: 80 },
                          { title: 'Odometer', dataIndex: 'odometer', key: 'odometer', width: 100 },
                          { title: 'Status', dataIndex: 'status', key: 'status', width: 80 }
                        ]}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  )}
                  {record.expenseRecords && record.expenseRecords.length > 0 && (
                    <div>
                      <h4>Other Expenses ({record.expenseRecords.length})</h4>
                      <Table
                        dataSource={record.expenseRecords}
                        rowKey="expense_id"
                        columns={[
                          { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
                          { title: 'Type', dataIndex: 'expense_type', key: 'expense_type' },
                          { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `$${val || 0}`, width: 80 },
                          { title: 'Description', dataIndex: 'description', key: 'description' }
                        ]}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  )}
                </div>
              ),
              rowExpandable: (record) => auditView === 'day' && (record.trips?.length > 0 || record.fuelRecords?.length > 0 || record.expenseRecords?.length > 0)
            } : undefined}
          />
        </Card>
      </div>
    );
  };



  return (
    <div>
      <h1>BI Reports</h1>
      
      <Alert
        message="Business Intelligence Dashboard"
        description="Comprehensive reports based on real operational data. Filter by truck to analyze individual performance."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Select
              placeholder="All Trucks"
              value={selectedTruck}
              onChange={setSelectedTruck}
              style={{ width: 250 }}
              allowClear
            >
              {trucks.map(truck => {
                const driver = drivers.find(d => d.truck_id === truck.truck_id);
                return (
                  <Option key={truck.truck_id} value={truck.truck_id}>
                    {truck.license_plate} {driver ? `(${driver.name})` : ''}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col>
            <RangePicker 
              value={dateRange} 
              onChange={setDateRange}
              placeholder={['Start Date', 'End Date']}
              style={{ borderColor: !dateRange ? '#ff4d4f' : undefined }}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={generateReports} loading={loading}>
              Generate Reports
            </Button>
          </Col>
          <Col>
            <Button 
              icon={<RobotOutlined />} 
              onClick={generateAIAnalysis}
              loading={analysisLoading}
              disabled={!dateRange}
            >
              AI Analysis
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Input.TextArea
              placeholder="Ask a specific question (optional) — e.g. 'Which routes are most profitable?' or 'Is the fuel consumption normal for TRK002?'"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </Col>
        </Row>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><FundOutlined />Revenue</span>} key="revenue">
          {renderRevenueTab()}
        </TabPane>
        <TabPane tab={<span><BarChartOutlined />Expenses</span>} key="expenses">
          {renderExpensesTab()}
        </TabPane>
        <TabPane tab={<span><CarOutlined />Fuel</span>} key="fuel">
          {renderFuelTab()}
        </TabPane>
        <TabPane tab={<span><DollarOutlined />Profitability</span>} key="profitability">
          {renderProfitabilityTab()}
        </TabPane>
      </Tabs>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RobotOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <span>AI Operations Analysis</span>
          </div>
        }
        open={analysisModal}
        onCancel={() => setAnalysisModal(false)}
        footer={[
          <Button 
            key="download" 
            type="primary" 
            icon={<FilePdfOutlined />} 
            onClick={downloadAIAnalysisPDF}
            loading={pdfLoading}
            disabled={!analysisData || analysisLoading}
          >
            Download PDF
          </Button>,
          <Button key="close" onClick={() => setAnalysisModal(false)}>
            Close
          </Button>
        ]}
        width={900}
      >
        {analysisLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#666' }}>Analyzing operations data with AI...</p>
          </div>
        ) : analysisData ? (
          <div>
            {analysisData.custom_prompt && (
              <Alert
                message="Your Question"
                description={analysisData.custom_prompt}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Total Revenue" value={analysisData.summary?.total_revenue || 0} prefix="$" />
                </Col>
                <Col span={6}>
                  <Statistic title="Total Expenses" value={(analysisData.summary?.total_fuel || 0) + (analysisData.summary?.total_expenses || 0)} prefix="$" />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Net Profit" 
                    value={analysisData.summary?.net_profit || 0} 
                    prefix="$"
                    valueStyle={{ color: (analysisData.summary?.net_profit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Profit Margin" 
                    value={analysisData.summary?.profit_margin || 0} 
                    suffix="%"
                  />
                </Col>
              </Row>
            </Card>
            <Card title="AI Insights" size="small">
              <div style={{ maxHeight: '50vh', overflow: 'auto', padding: '0 8px' }}>
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h2 style={{ color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: 8, marginTop: 16 }}>{children}</h2>,
                    h2: ({children}) => <h3 style={{ color: '#1890ff', marginTop: 20, marginBottom: 8 }}>{children}</h3>,
                    h3: ({children}) => <h4 style={{ color: '#333', marginTop: 16, marginBottom: 6 }}>{children}</h4>,
                    p: ({children}) => <p style={{ marginBottom: 8, lineHeight: 1.6 }}>{children}</p>,
                    ul: ({children}) => <ul style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ul>,
                    ol: ({children}) => <ol style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ol>,
                    li: ({children}) => <li style={{ marginBottom: 4 }}>{children}</li>,
                    strong: ({children}) => <strong style={{ color: '#333' }}>{children}</strong>,
                    table: ({children}) => <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>{children}</table>,
                    th: ({children}) => <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#fafafa', textAlign: 'left' }}>{children}</th>,
                    td: ({children}) => <td style={{ border: '1px solid #d9d9d9', padding: '8px' }}>{children}</td>,
                  }}
                >
                  {analysisData.analysis}
                </ReactMarkdown>
              </div>
            </Card>
            <div style={{ marginTop: 12, fontSize: 12, color: '#999', textAlign: 'right' }}>
              Generated: {new Date(analysisData.generated_at).toLocaleString()}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Reports;